import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';
import { toast } from "sonner";
import { 
  getUserByUsername, 
  signInWithPassword, 
  signUpNewUser, 
  signOut,
} from '@/services/supabaseAuth';
import { getProfile } from '@/services/supabaseUsers';
import { Tables } from '@/integrations/supabase/types';
import SessionWarningDialog from '@/components/SessionWarningDialog';

type Profile = Tables<'profiles'>;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<{ error: string | null }>;
  signup: (email: string, password: string, username: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const SupabaseAuthContext = createContext<AuthContextType | undefined>(undefined);

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [isSessionExpiring, setIsSessionExpiring] = useState(false);
  const navigate = useNavigate();
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Activity detection: reset session warning timer on user activity
  useEffect(() => {
    const handleActivity = () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      setIsSessionExpiring(false);
      setShowSessionWarning(false);
      // Reset session warning timer (30 min inactivity)
      activityTimeoutRef.current = setTimeout(() => {
        setIsSessionExpiring(true);
        setShowSessionWarning(true);
      }, 30 * 60 * 1000); // 30 minutes
    };
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => document.addEventListener(event, handleActivity, { passive: true }));
    handleActivity(); // Start timer on mount
    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
  }, []);

  // Session timeout handling (with warning dialog)
  useEffect(() => {
    const fetchSession = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("Error getting session:", sessionError);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };
    fetchSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });
    let interval: NodeJS.Timeout;
    function checkSessionTimeout() {
      if (!session) return;
      const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
      if (!expiresAt) return;
      const now = new Date();
      const timeLeft = expiresAt.getTime() - now.getTime();
      setSessionTimeLeft(timeLeft);
      // Show warning 5 minutes before expiry
      if (timeLeft <= 5 * 60 * 1000 && timeLeft > 0) {
        setIsSessionExpiring(true);
        setShowSessionWarning(true);
      }
      // Auto-logout if expired
      if (timeLeft <= 0) {
        toast.error('Session expired. Please log in again.');
        setShowSessionWarning(false);
        setIsSessionExpiring(false);
        logout();
      }
    }
    interval = setInterval(checkSessionTimeout, 15 * 60 * 1000); // 15 minutes
    checkSessionTimeout();
    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [session]);

  // Handler to extend session (refresh)
  const handleExtendSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      setShowSessionWarning(false);
      setIsSessionExpiring(false);
      toast.success('Session extended successfully');
    } catch (error) {
      toast.error('Failed to extend session. Please log in again.');
      setShowSessionWarning(false);
      setIsSessionExpiring(false);
      logout();
    }
  };

  const handleLogout = () => {
    setShowSessionWarning(false);
    setIsSessionExpiring(false);
    logout();
  };

  const login = async (identifier: string, password: string): Promise<{ error: string | null }> => {
    try {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
      let email = identifier;

      if (!isEmail) {
        const userProfile = await getUserByUsername(identifier);
        if (!userProfile || !userProfile.length) {
          return { error: 'Invalid username or password' };
        }
        email = userProfile[0].user_email;
      }

      const { data, error } = await signInWithPassword({ email, password });

      if (error) {
        return { error: error.message };
      }

      if (data.user) {
        navigate('/dashboard');
        return { error: null };
      }

      return { error: 'Invalid login. Please try again.' };
    } catch (err) {
      console.error('Login error:', err);
      return { error: 'An unexpected error occurred' };
    }
  };

  const signup = async (
    email: string,
    password: string,
    username: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error } = await signUpNewUser({ email, password, username });

      if (error) {
        return { error: error.message };
      }

      toast.success('Registration successful! Please check your email to confirm your account.');
      return { error: null };
    } catch (err) {
      console.error('Signup error:', err);
      return { error: 'An unexpected error occurred during signup' };
    }
  };

  const logout = async () => {
    await signOut();
    setSession(null);
    setUser(null);
    navigate('/auth');
  };

  const value = {
    user,
    session,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!session
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
      <SessionWarningDialog
        open={showSessionWarning}
        timeLeft={sessionTimeLeft}
        onExtend={handleExtendSession}
        onLogout={handleLogout}
        onDismiss={() => setShowSessionWarning(false)}
      />
    </SupabaseAuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};
